/**
 * The activation screen — code entry + mandatory GPS grant. Gates the whole
 * player-facing app; see src/ui/activation.ts and relay/activation.ts for
 * the full design. GPS is required with no fallback: rejecting it means the
 * app genuinely cannot be used, by design (see the design discussion on why
 * IP-based location isn't trusted here).
 */

import { activate, queryGeoPermission, readLocation } from '../activation';
import { readBranding, renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import { knownLeaderboardEnabled, refreshPublicLeaderboardEnabled } from '../leaderboardFlag';

export interface ActivationActions {
  success(): void;
}

type Step =
  | { kind: 'enterCode' }
  | { kind: 'requestingLocation'; code: string }
  | { kind: 'locationBlocked'; code: string }
  | { kind: 'activating'; code: string }
  | { kind: 'error'; code: string; message: string; expandTerms?: boolean };

/**
 * Uses the current branding name (not a hardcoded venue name) so this never
 * drifts out of sync with whatever's shown in the header — see
 * src/ui/branding.ts. Wording finalized with Indra; see the memory note on
 * why each clause reads the way it does before changing it casually.
 */
export function termsOfUseHtml(opts: { leaderboard?: boolean } = {}): string {
  const leaderboard = opts.leaderboard !== false;
  const venueName = readBranding().name;
  return `
    <p><strong>Use at the venue.</strong> This app is for players physically present at ${esc(venueName)}. It needs an
    activation code from venue staff and a location check. Using it away from the venue is not permitted and may get your
    device denied access.</p>
    <p><strong>Recreational only.</strong> No round involves real money, and points cannot be exchanged for cash, goods or
    anything of value.</p>
    <p><strong>Location and device data.</strong> We check your GPS location when you activate the app, ${
      leaderboard ? 'when you start a game, and when you open the leaderboard' : 'and when you start a game'
    }. Your exact position is not kept. We keep,
    for about a day, only: which venue you were nearest to and roughly how far away you were; the approximate city and country
    of your internet connection; and a random code for your device. This lets venue admin staff see that a device is really at
    the venue. It is deleted when your session ends.</p>
    <p><strong>Sharing a game.</strong> If you share a game with other phones, its player names, scores and rounds are stored on
    our server for up to 24 hours so guests can see them. A game you do not share stays on your phone.</p>
    ${leaderboard ? leaderboardTermsHtml() : ''}
    <p>By entering an activation code, you agree to these terms and to the location check.</p>
  `;
}

/**
 * The part of the Terms that is only about the Leaderboard. Left out of the activation screen's Terms while the leaderboard is switched
 * off (Indra, 2026-09-27), and shown to the host when they open a table so it is always on the record before a leaderboard game.
 */
export function leaderboardTermsHtml(): string {
  return `
    <p><strong>Leaderboard Data</strong><br />
    If you take part, you create a User ID (a nickname and a 4-digit PIN) on your own phone and can use it straight away.
    Before a leaderboard game starts, venue staff may check the table — its name, the venue, the time and the four User IDs —
    and approve it. A table that staff do not answer expires after two hours and is deleted within a day.</p>
    <p>We keep your nickname, your PIN in scrambled form that nobody can read back, your game results, the other players at
    your table, and an anonymous device identifier. We do not collect your name, email address or phone number. Your data is
    stored with our hosting provider and used only to run the leaderboard, calculate rankings, verify results, and detect
    duplicate or suspicious games.</p>
    <p>Your nickname and score may be seen by other players and shown on the venue's TV.</p>
    <p>If you forget your PIN, staff can unlock your account and give you a temporary PIN, and you then choose your own.
    Staff never choose or see your PIN.</p>
    <p>Game results are deleted 90 days after the period ends, and an account after 90 days without playing. You may ask venue
    staff to delete your account at any time. Taking part is voluntary; the venue's other services stay available.</p>
  `;
}

export function renderActivation(root: HTMLElement, actions: ActivationActions): void {
  render({ kind: 'enterCode' }, root, actions);
}

function render(step: Step, root: HTMLElement, actions: ActivationActions): void {
  switch (step.kind) {
    case 'enterCode':
      return renderEnterCode(step, root, actions);
    case 'requestingLocation':
      return renderRequestingLocation(step, root, actions);
    case 'locationBlocked':
      return renderLocationBlocked(step, root, actions);
    case 'activating':
      return renderActivating(step, root, actions);
    case 'error':
      return renderError(step, root, actions);
  }
}

function renderEnterCode(
  step: { kind: 'enterCode' } | { kind: 'error'; code: string; message: string; expandTerms?: boolean },
  root: HTMLElement,
  actions: ActivationActions,
): void {
  const error = step.kind === 'error' ? step.message : undefined;
  const prefill = step.kind === 'error' ? step.code : '';
  // Re-render already-expanded when we're bouncing someone back here for
  // not having opened it — so they land straight on the text to read,
  // rather than having to go find the toggle a second time.
  const startExpanded = step.kind === 'error' && step.expandTerms === true;

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Enter Activation Code</h1>
      <p class="muted">Ask staff for today's code to start playing.</p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="codeInput">Activation code</label>
      <input type="text" id="codeInput" autocomplete="off" autocapitalize="characters" value="${esc(prefill)}" />

      <div style="margin-top:0.75rem">
        <button type="button" id="termsToggle" class="ghost" style="width:100%;text-align:left;font-size:0.85rem">
          Terms of Use ${startExpanded ? '▴' : '▾'}
        </button>
        <div id="termsBody" class="muted"
          style="display:${startExpanded ? 'block' : 'none'};font-size:0.8rem;margin-top:0.5rem;line-height:1.5">
          ${termsOfUseHtml({ leaderboard: knownLeaderboardEnabled() !== false })}
        </div>
      </div>

      <button type="button" id="continueBtn" style="width:100%;margin-top:1rem">Continue</button>
    </div>
  `;

  if (error) window.scrollTo(0, 0);

  let termsOpened = startExpanded;
  const termsToggle = root.querySelector<HTMLButtonElement>('#termsToggle')!;
  const termsBody = root.querySelector<HTMLElement>('#termsBody')!;

  // The Terms only talk about the leaderboard while it is switched on. A phone with no session cannot ask the usual way, so it asks
  // the relay's public answer; until then the FULL terms are shown (unknown = full). If the answer ADDS text after the terms were
  // already opened, that text must be read, so they must be opened again; if it only takes the leaderboard part away, what was read
  // was a superset of what now stands, so nothing needs re-reading (and Continue must not look dead because of it).
  void refreshPublicLeaderboardEnabled().then((enabled) => {
    if (enabled === undefined || !root.contains(termsBody)) return;
    // Compared as the browser holds them: what is on screen has been re-written by the browser (a `<br />` is kept as `<br>`), so the
    // template text itself never equals it, and every answer used to count as a change and make the Terms unread.
    const next = document.createElement('div');
    next.innerHTML = termsOfUseHtml({ leaderboard: enabled });
    if (next.innerHTML.trim() === termsBody.innerHTML.trim()) return;
    const grew = (next.textContent ?? '').length > (termsBody.textContent ?? '').length;
    termsBody.innerHTML = next.innerHTML;
    if (grew) termsOpened = false;
  });
  termsToggle.addEventListener('click', () => {
    const opening = termsBody.style.display === 'none';
    termsBody.style.display = opening ? 'block' : 'none';
    termsToggle.textContent = `Terms of Use ${opening ? '▴' : '▾'}`;
    if (opening) termsOpened = true;
  });

  root.querySelector<HTMLButtonElement>('#continueBtn')!.addEventListener('click', () => {
    const code = root.querySelector<HTMLInputElement>('#codeInput')!.value.trim();
    if (!code) {
      render({ kind: 'error', code: '', message: 'Enter the activation code.' }, root, actions);
      return;
    }
    if (!termsOpened) {
      render(
        { kind: 'error', code, message: 'Please review the Terms of Use before continuing.', expandTerms: true },
        root,
        actions,
      );
      return;
    }
    // At once, before anything is awaited: the button shows it was pressed and cannot be pressed twice.
    const button = root.querySelector<HTMLButtonElement>('#continueBtn')!;
    button.disabled = true;
    button.textContent = 'Checking…';
    void handleLocationStep(code, root, actions);
  });
}

async function handleLocationStep(code: string, root: HTMLElement, actions: ActivationActions): Promise<void> {
  const permission = await queryGeoPermission();

  if (permission === 'unsupported') {
    render(
      {
        kind: 'error',
        code,
        message: 'This browser does not support location access, which this app requires. Try a different browser.',
      },
      root,
      actions,
    );
    return;
  }

  if (permission === 'denied') {
    render({ kind: 'locationBlocked', code }, root, actions);
    return;
  }

  render({ kind: 'requestingLocation', code }, root, actions);
  const result = await readLocation();

  if (!result.ok) {
    if (result.reason === 'denied') {
      // Re-check: the browser may now remember this as a permanent block
      // rather than a one-off rejection, which changes what to tell them.
      const after = await queryGeoPermission();
      if (after === 'denied') {
        render({ kind: 'locationBlocked', code }, root, actions);
      } else {
        render(
          { kind: 'error', code, message: 'Location access was declined. Grant it to continue — you can try again.', expandTerms: true },
          root,
          actions,
        );
      }
    } else {
      render(
        { kind: 'error', code, message: 'Could not read your location. Check your connection and try again.', expandTerms: true },
        root,
        actions,
      );
    }
    return;
  }

  render({ kind: 'activating', code }, root, actions);
  const activated = await activate(code, result.coords);
  if (!activated.ok) {
    render({ kind: 'error', code, message: activated.error, expandTerms: true }, root, actions);
    return;
  }
  actions.success();
}

function renderRequestingLocation(
  _step: { kind: 'requestingLocation'; code: string },
  root: HTMLElement,
  _actions: ActivationActions,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Requesting Location…</h1>
      <p class="muted">Your browser should be asking for location access now — allow it to continue.</p>
    </div>
  `;
}

function renderActivating(
  _step: { kind: 'activating'; code: string },
  root: HTMLElement,
  _actions: ActivationActions,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Checking Code…</h1>
    </div>
  `;
}

function renderLocationBlocked(
  step: { kind: 'locationBlocked'; code: string },
  root: HTMLElement,
  actions: ActivationActions,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Location Access Is Blocked</h1>
      <p class="muted">
        Your browser has location access turned off for this site, so it isn't showing the permission prompt again.
        This app requires location access to continue.
      </p>
      <p class="muted">
        To fix it: open your browser's site settings for this page (often the icon next to the address bar, or
        Settings → Site permissions → Location), allow location, then try again.
      </p>
      <button type="button" id="retryBtn" style="width:100%;margin-top:1rem">I've Allowed It — Try Again</button>
      <button type="button" id="backBtn" class="secondary" style="width:100%;margin-top:0.5rem">Back</button>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#retryBtn')!.addEventListener('click', () => {
    void handleLocationStep(step.code, root, actions);
  });
  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => {
    render({ kind: 'enterCode' }, root, actions);
  });
}

function renderError(
  step: { kind: 'error'; code: string; message: string; expandTerms?: boolean },
  root: HTMLElement,
  actions: ActivationActions,
): void {
  renderEnterCode(step, root, actions);
}
